import type { ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Card, CardContent } from '@design-system';

interface RepeatableEntriesProps<T> {
  entries: T[];
  onChange: (entries: T[]) => void;
  createEntry: () => T;
  entryTitle: (entry: T, index: number) => string;
  addLabel: string;
  // The first entry can't be removed when at least one is expected
  keepFirst?: boolean;
  renderEntry: (entry: T, update: (patch: Partial<T>) => void) => ReactNode;
}

export function RepeatableEntries<T>({
  entries,
  onChange,
  createEntry,
  entryTitle,
  addLabel,
  keepFirst = false,
  renderEntry,
}: RepeatableEntriesProps<T>) {
  const update = (index: number) => (patch: Partial<T>) =>
    onChange(entries.map((entry, position) => (position === index ? { ...entry, ...patch } : entry)));
  const remove = (index: number) => onChange(entries.filter((_, position) => position !== index));

  return (
    <div className="flex flex-col gap-4">
      {entries.map((entry, index) => (
        <Card key={index} className="gap-3 py-4" data-testid="consumer-application-entry">
          <CardContent className="flex flex-col gap-4 px-4">
            <div className="flex items-center justify-between">
              <strong className="text-gray-950">{entryTitle(entry, index)}</strong>
              {!(keepFirst && index === 0) && (
                <Button variant="ghost" size="sm" onClick={() => remove(index)}>
                  <Trash2 />
                  Remove
                </Button>
              )}
            </div>
            {renderEntry(entry, update(index))}
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" className="self-start" onClick={() => onChange([...entries, createEntry()])}>
        <Plus />
        {addLabel}
      </Button>
    </div>
  );
}
